import React, { useEffect, useMemo, useState } from "react";
import { channelPartnerApi, toApiAbsoluteUrl } from "../../api/client";
import { Link } from "react-router-dom";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import {
  ChevronRight,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  UserX,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function Promoter() {
  const { user } = useAuth();
  const [promoters, setPromoters] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [verificationModal, setVerificationModal] = useState({
    open: false,
    promoter: null,
    action: null,
    type: null, // 'payment' or 'profile'
  });
  const [verificationNotes, setVerificationNotes] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  // const navigate = useNavigate(); // Will be used for navigation to promoter details
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  const toAbsoluteUrl = toApiAbsoluteUrl;

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    if (!bytes || Number.isNaN(bytes)) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${
      sizes[i]
    }`;
  }

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    if (user?.id || user?._id) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, user?.id, user?._id]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };

    // Get the logged-in channel partner's ID
    const channelPartnerId = user?.id || user?._id;

    if (!channelPartnerId) {
      setPromoters([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);

      const res = await channelPartnerApi.listPromoters({
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
      });

      setPromoters(res?.promoters || []);
      setTotal(res?.pagination?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      console.error("Promoters API error:", _e);
      setPromoters([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load promoters";
      setSnackbar({ open: true, message: msg });
    } finally {
      setLoading(false);
    }
  }

  const filteredPromoters = useMemo(() => {
    if (!normalizedQuery) return promoters;
    return (promoters || []).filter((p) => {
      const email = (p?.email || "").toLowerCase();
      const phone = (p?.userId?.phoneNumber || "").toLowerCase();
      const name = (p?.name || "").toLowerCase();
      const fatherName = (p?.fatherName || "").toLowerCase();
      const panNumber = (p?.panNumber || "").toLowerCase();
      const bloodGroup = (p?.bloodGroup || "").toLowerCase();
      const qualification = (p?.qualification || "").toLowerCase();
      const address = p?.address;
      const addressStr = address
        ? `${address.street || ""} ${address.city || ""} ${
            address.state || ""
          } ${address.pincode || ""}`.toLowerCase()
        : "";

      return (
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        name.includes(normalizedQuery) ||
        fatherName.includes(normalizedQuery) ||
        panNumber.includes(normalizedQuery) ||
        bloodGroup.includes(normalizedQuery) ||
        qualification.includes(normalizedQuery) ||
        addressStr.includes(normalizedQuery)
      );
    });
  }, [promoters, normalizedQuery, searchText]);

  // Verification functions
  const openVerificationModal = (promoter, action, type) => {
    setVerificationModal({ open: true, promoter, action, type });
    setVerificationNotes("");
  };

  const closeVerificationModal = () => {
    setVerificationModal({
      open: false,
      promoter: null,
      action: null,
      type: null,
    });
    setVerificationNotes("");
  };

  const handleVerification = async () => {
    if (!verificationModal.promoter || !verificationModal.action) return;

    setVerificationLoading(true);
    try {
      const isVerified = verificationModal.action === "verify";

      // Verify both profile and payment together
      const profileResult = await channelPartnerApi.verifyPromoter(
        verificationModal.promoter._id,
        isVerified,
        verificationNotes
      );

      // Also verify payment if profile verification is successful
      if (isVerified) {
        await channelPartnerApi.verifyPromoterPayment(
          verificationModal.promoter._id,
          isVerified,
          verificationNotes
        );
      }

      setSnackbar({
        open: true,
        message: `Promoter ${
          isVerified ? "verified" : "rejected"
        } successfully`,
        severity: "success",
      });

      // Refresh the promoters list
      await runSearch();

      closeVerificationModal();
    } catch (error) {
      console.error("Verification error:", error);
      setSnackbar({
        open: true,
        message:
          error?.data?.message ||
          `Failed to ${verificationModal.action} promoter`,
        severity: "error",
      });
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/channelpartner/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">My Promoters</span>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Channel Partner View:</strong> This page shows only promoters
          that are linked to your channel partner account.
        </p>
      </div>

      {/* Search bar */}
      <div className="bg-white shadow rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search name, email, phone, PAN, father's name, or address"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSearchText("");
              setFilters({ q: "", page: 1, limit: filters.limit });
              runSearch({ page: 1 });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Responsive table */}
      <div className="overflow-x-auto">
        <table className="w-full text-center bg-white shadow rounded min-w-[1600px]">
          <thead>
            <tr>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Father's Name</th>
              <th className="p-2 border">PAN</th>
              <th className="p-2 border">Blood Group</th>
              <th className="p-2 border">Qualification</th>
              <th className="p-2 border">Address</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Payment Status</th>
              <th className="p-2 border">Payment Amount</th>
              <th className="p-2 border">UTR ID</th>
              <th className="p-2 border">Commission</th>
              <th className="p-2 border">Documents</th>
              <th className="p-2 border">Verified At</th>
              <th className="p-2 border">Created</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPromoters.map((p) => {
              const name = p?.name || "-";
              const status = p?.status || "pending";
              const paymentStatus =
                p?.userId?.paymentStatus ||
                p?.paymentDetails?.paymentStatus ||
                "pending";
              const docs = p?.documents || [];

              return (
                <tr key={p._id}>
                  <td className="p-2 border">
                    {name !== "-" ? (
                      <button
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={() => setSelectedRecord(p)}
                        title="View promoter details"
                      >
                        {name}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 border text-sm">{p.email}</td>
                  <td className="p-2 border text-sm">
                    {p?.userId?.phoneNumber || "-"}
                  </td>
                  <td className="p-2 border text-sm">{p?.fatherName || "-"}</td>
                  <td className="p-2 border text-sm">{p?.panNumber || "-"}</td>
                  <td className="p-2 border text-sm">{p?.bloodGroup || "-"}</td>
                  <td className="p-2 border text-sm">
                    {p?.qualification || "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.address
                      ? `${p.address.street || ""} ${p.address.city || ""} ${
                          p.address.state || ""
                        } ${p.address.pincode || ""}`.trim() || "-"
                      : "-"}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        status === "approved"
                          ? "bg-green-100 text-green-800"
                          : status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : status === "pending_verification"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        paymentStatus === "verified"
                          ? "bg-green-100 text-green-800"
                          : paymentStatus === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {paymentStatus}
                    </span>
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.paymentDetails?.amount
                      ? `₹${p.paymentDetails.amount}`
                      : "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.paymentDetails?.utrId || p?.userId?.utrId || "-"}
                  </td>
                  <td className="p-2 border">
                    {p?.commissionRate ? `${p.commissionRate}%` : "-"}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        docs.length > 0
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {docs.length} docs
                    </span>
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.verifiedAt ? formatDate(p.verifiedAt) : "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {formatDate(p?.createdAt)}
                  </td>
                  <td className="p-2 border">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setSelectedRecord(p)}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                      >
                        View
                      </button>

                      {/* Promoter Verification Buttons (verifies both profile and payment) */}
                      {status === "pending_verification" && (
                        <div className="flex gap-1">
                          <button
                            onClick={() =>
                              openVerificationModal(p, "verify", "promoter")
                            }
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors flex items-center gap-1"
                            title="Verify Promoter (Profile + Payment)"
                          >
                            <UserCheck size={12} />
                            Verify
                          </button>
                          <button
                            onClick={() =>
                              openVerificationModal(p, "reject", "promoter")
                            }
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors flex items-center gap-1"
                            title="Reject Promoter"
                          >
                            <UserX size={12} />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredPromoters.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={17}>
                  No promoters found under your channel partner account
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-3 flex-wrap gap-2">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={promoters.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Promoter Details</h3>
              <button
                onClick={() => setSelectedRecord(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-blue-800">
                  Personal Information
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Name:</strong> {selectedRecord.name || "-"}
                  </p>
                  <p>
                    <strong>Father's Name:</strong>{" "}
                    {selectedRecord.fatherName || "-"}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedRecord.email || "-"}
                  </p>
                  <p>
                    <strong>Phone:</strong>{" "}
                    {selectedRecord?.userId?.phoneNumber ||
                      selectedRecord.phoneNumber ||
                      "-"}
                  </p>
                  <p>
                    <strong>Blood Group:</strong>{" "}
                    {selectedRecord.bloodGroup || "-"}
                  </p>
                  <p>
                    <strong>Qualification:</strong>{" "}
                    {selectedRecord.qualification || "-"}
                  </p>
                  <p>
                    <strong>PAN Number:</strong>{" "}
                    {selectedRecord.panNumber || "-"}
                  </p>
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-green-800">
                  Address
                </h4>
                {selectedRecord.address ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Street:</strong>{" "}
                      {selectedRecord.address.street || "-"}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {selectedRecord.address.city || "-"}
                    </p>
                    <p>
                      <strong>State:</strong>{" "}
                      {selectedRecord.address.state || "-"}
                    </p>
                    <p>
                      <strong>Pincode:</strong>{" "}
                      {selectedRecord.address.pincode || "-"}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {selectedRecord.address.country || "-"}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">No address provided</p>
                )}
              </div>

              {/* Status & Payment Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-purple-800">
                  Status & Payment
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Profile Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        selectedRecord.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : selectedRecord.status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedRecord.status === "pending_verification"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedRecord.status?.replace(/_/g, " ") || "-"}
                    </span>
                  </p>
                  <p>
                    <strong>Payment Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        selectedRecord?.userId?.paymentStatus === "verified"
                          ? "bg-green-100 text-green-800"
                          : selectedRecord?.userId?.paymentStatus === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {selectedRecord?.userId?.paymentStatus ||
                        selectedRecord?.paymentDetails?.paymentStatus ||
                        "-"}
                    </span>
                  </p>
                  <p>
                    <strong>Payment Amount:</strong>{" "}
                    {selectedRecord?.paymentDetails?.amount
                      ? `₹${selectedRecord.paymentDetails.amount}`
                      : "-"}
                  </p>
                  <p>
                    <strong>UTR ID:</strong>{" "}
                    {selectedRecord?.paymentDetails?.utrId ||
                      selectedRecord?.userId?.utrId ||
                      "-"}
                  </p>
                  <p>
                    <strong>Commission Rate:</strong>{" "}
                    {selectedRecord.commissionRate
                      ? `${selectedRecord.commissionRate}%`
                      : "-"}
                  </p>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-orange-800">
                  Statistics
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Providers Created:</strong>{" "}
                    {selectedRecord.totalProvidersCreated || 0}
                  </p>
                  <p>
                    <strong>Listings Under Promoter:</strong>{" "}
                    {selectedRecord.totalListingsUnderPromoter || 0}
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {selectedRecord.createdAt
                      ? formatDate(selectedRecord.createdAt)
                      : "-"}
                  </p>
                  <p>
                    <strong>Verified At:</strong>{" "}
                    {selectedRecord.verifiedAt
                      ? formatDate(selectedRecord.verifiedAt)
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mt-6">
              <h4 className="font-semibold text-lg mb-3 text-red-800">
                Documents (
                {(() => {
                  const docs = selectedRecord.documents;
                  if (Array.isArray(docs)) {
                    return docs.length;
                  } else if (docs && typeof docs === "object") {
                    return Object.keys(docs).length;
                  }
                  return 0;
                })()}
                )
              </h4>
              <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                {(() => {
                  const docs = selectedRecord.documents;
                  const hasDocuments = Array.isArray(docs)
                    ? docs.length > 0
                    : docs &&
                      typeof docs === "object" &&
                      Object.keys(docs).length > 0;

                  if (!hasDocuments) {
                    return (
                      <div className="p-4 text-sm text-gray-500">
                        No documents uploaded
                      </div>
                    );
                  }

                  // Handle array format (full document objects)
                  if (Array.isArray(docs)) {
                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Preview</th>
                            <th className="p-2 border">Name</th>
                            <th className="p-2 border">Type</th>
                            <th className="p-2 border">Size</th>
                            <th className="p-2 border">Uploaded</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const isImage = (d?.mimeType || "").startsWith(
                              "image/"
                            );
                            return (
                              <tr key={d.id}>
                                <td className="p-2 border align-middle">
                                  {isImage ? (
                                    <button
                                      className="inline-flex items-center"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                      title="Open image"
                                    >
                                      <img
                                        src={toAbsoluteUrl(d.url)}
                                        alt={
                                          d.originalName || d.kind || "document"
                                        }
                                        className="h-12 w-12 object-cover rounded"
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex items-center text-gray-600">
                                      <FileText size={18} className="mr-1" />
                                      <span>File</span>
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.originalName || d.kind || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.mimeType || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatBytes(d.size)}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatDate(d.uploadedAt)}
                                </td>
                                <td className="p-2 border align-middle">
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                    >
                                      Open
                                    </button>
                                    <a
                                      className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                      href={toAbsoluteUrl(d.url)}
                                      download={d.originalName || true}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Download
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  }

                  // Handle object format (document IDs)
                  if (docs && typeof docs === "object") {
                    const documentTypes = {
                      experienceDocuments: "Experience Documents",
                      panCardImage: "PAN Card",
                      kycDocument: "KYC Document",
                      kycImage: "KYC Image",
                      cvUpload: "CV Upload",
                      photoUpload: "Photo Upload",
                    };

                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Document Type</th>
                            <th className="p-2 border">Document ID</th>
                            <th className="p-2 border">Status</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(docs).map(([key, docId]) => (
                            <tr key={key}>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center text-gray-600">
                                  <FileText size={18} className="mr-2" />
                                  <span className="font-medium">
                                    {documentTypes[key] || key}
                                  </span>
                                </div>
                              </td>
                              <td className="p-2 border align-middle text-sm">
                                {docId}
                              </td>
                              <td className="p-2 border align-middle">
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  Uploaded
                                </span>
                              </td>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center gap-2">
                                  <button
                                    className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                    onClick={() =>
                                      window.open(
                                        toAbsoluteUrl(`/uploads/${docId}`),
                                        "_blank",
                                        "noopener"
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                  <a
                                    className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                    href={toAbsoluteUrl(`/uploads/${docId}`)}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }

                  return (
                    <div className="p-4 text-sm text-gray-500">
                      No documents uploaded
                    </div>
                  );
                })()}
              </div>
            </div>
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Verification Modal */}
      {verificationModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {verificationModal.action === "verify" ? "Verify" : "Reject"}{" "}
                Promoter
              </h3>
              <button
                onClick={closeVerificationModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={verificationLoading}
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-semibold">
                  {verificationModal.promoter?.name}
                </p>
                <p className="text-sm text-gray-600">
                  {verificationModal.promoter?.email}
                </p>
                <p className="text-sm text-gray-600">
                  UTR ID:{" "}
                  {verificationModal.promoter?.paymentDetails?.utrId || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Amount: ₹
                  {verificationModal.promoter?.paymentDetails?.amount || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Status: {verificationModal.promoter?.status || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {verificationModal.action === "verify"
                    ? "Verification Notes (Optional)"
                    : "Reason"}
                </label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder={
                    verificationModal.action === "verify"
                      ? "Add any notes about the verification..."
                      : "Please provide a reason..."
                  }
                  className="w-full border p-3 rounded-md resize-none"
                  rows={3}
                  disabled={verificationLoading}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeVerificationModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={verificationLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerification}
                  className={`px-4 py-2 rounded text-white transition-colors flex items-center gap-2 ${
                    verificationModal.action === "verify"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  } ${
                    verificationLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={verificationLoading}
                >
                  {verificationLoading ? (
                    <>
                      <Clock size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {verificationModal.action === "verify" ? (
                        <>
                          <CheckCircle size={16} />
                          Verify Promoter
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          Reject Promoter
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({ open: false, message: "", severity: "error" })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbar({ open: false, message: "", severity: "error" })
          }
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
